// ABOUTME: Fetches the site-wide Crossing content library once and exposes it keyed by code for O(1) lookups.
// ABOUTME: Every Crossing screen resolves copy through this hook — never a hardcoded string (Issue #1938).

import { createContext, useState, useEffect, useCallback, useContext } from 'react';
import api from '../services/api';
import type {
  CrossingContentDto,
  CrossingTooMuchCategoryDto,
  CrossingStormDto,
  CrossingReachTypeDto,
  CrossingFeelingDto,
  CrossingWeatherPatternDto,
  CrossingSteersmanTipDto,
} from '../types/api';

interface CrossingContentByCode {
  tooMuchCategories: Record<string, CrossingTooMuchCategoryDto>;
  weatherPatterns: Record<string, CrossingWeatherPatternDto>;
  storms: Record<string, CrossingStormDto>;
  reachTypes: Record<string, CrossingReachTypeDto>;
  feelings: Record<string, CrossingFeelingDto>;
  steersmanTips: Record<string, CrossingSteersmanTipDto>;
}

function byCode<T extends { code: string }>(items: T[]): Record<string, T> {
  return Object.fromEntries(items.map(item => [item.code, item]));
}

function toByCode(payload: CrossingContentDto): CrossingContentByCode {
  return {
    tooMuchCategories: byCode(payload.tooMuchCategories),
    weatherPatterns: byCode(payload.weatherPatterns),
    storms: byCode(payload.storms),
    reachTypes: byCode(payload.reachTypes),
    feelings: byCode(payload.feelings),
    steersmanTips: Object.fromEntries(payload.steersmanTips.map(tip => [tip.screenId, tip])),
  };
}

// Simple in-memory cache, matching useSiteSettings.ts's pattern.
let cachedContent: CrossingContentByCode | null = null;

// Every mounted hook instance hears about a reload, so a refetch by one reaches the screen reading the library too.
const listeners = new Set<(content: CrossingContentByCode) => void>();

export function invalidateCrossingContentCache() {
  cachedContent = null;
}

// Who is asking: a child link (slug + token) reads the library through its own route, a parent through the JWT route.
export interface CrossingContentSource {
  slug?: string;
  token?: string;
}

// Lets the runner frame hand its child link to every screen below it without threading props through each one.
export const CrossingContentSourceContext = createContext<CrossingContentSource>({});

export function useCrossingContent(source?: CrossingContentSource) {
  const contextSource = useContext(CrossingContentSourceContext);
  const { slug, token } = source ?? contextSource;
  const [data, setData] = useState<CrossingContentByCode | undefined>(cachedContent || undefined);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(!cachedContent);

  const fetchContent = useCallback(async () => {
    if (cachedContent) {
      setData(cachedContent);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = slug
        ? await api.get<CrossingContentDto>(`/api/child/${encodeURIComponent(slug)}/crossing/content`, { params: token ? { token } : {} })
        : await api.get<CrossingContentDto>('/api/crossing/content');
      cachedContent = toByCode(response.data);
      listeners.forEach(listener => listener(cachedContent!));
      setData(cachedContent);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch Crossing content'));
    } finally {
      setIsLoading(false);
    }
  }, [slug, token]);

  useEffect(() => {
    listeners.add(setData);
    fetchContent();
    return () => { listeners.delete(setData); };
  }, [fetchContent]);

  // Drops the cache and loads the library again -- used when the server says a code the child picked is gone.
  const refetch = useCallback(() => {
    invalidateCrossingContentCache();
    return fetchContent();
  }, [fetchContent]);

  return { data, error, isLoading, refetch };
}
